import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditorPanel } from "../components/EditorPanel";
import { SidePanel } from "../components/SidePanel";
import { Layout } from "./Layout";
import { detectLanguage } from "../utils/detectLanguage";
import {
  applyDetectedSource,
  isDirection,
  isScript,
  swapDirection,
  withFrom,
  withTo,
} from "../utils/direction";
import { storage } from "../lib/storage";

interface ApiResponse {
  message: string;
  result?: string;
}

export function HomePage() {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState<string>("");
  const [outputText, setOutputText] = useState<string>("");
  const [selectedDirection, setSelectedDirection] =
    useState<string>("lattocyr");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAutoDetected, setIsAutoDetected] = useState<boolean>(false);

  // Load last translation mode from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedMod = storage.getItem("lastTranslationMod");
      if (isDirection(savedMod)) {
        setSelectedDirection(savedMod);
      }
    }
  }, []);

  const handleTranslit = useCallback(async (): Promise<void> => {
    console.log("handleTranslit called, inputText length:", inputText.length);

    if (!inputText.trim()) {
      setOutputText("");
      setIsAutoDetected(false);
      return;
    }

    setIsLoading(true);
    try {
      const MAX_CHARS = 5000;
      const textToTranslate =
        inputText.length > MAX_CHARS
          ? inputText.substring(0, MAX_CHARS)
          : inputText;

      console.log(
        "Original length:",
        inputText.length,
        "Text to translate length:",
        textToTranslate.length,
      );
      console.log(
        "Text to translate:",
        textToTranslate.substring(0, 100) + "...",
      );

      const detection = detectLanguage(textToTranslate);
      let directionToUse = selectedDirection;
      if (detection.confidence > 70) {
        directionToUse = applyDetectedSource(
          selectedDirection,
          detection.direction,
        );
        if (directionToUse !== selectedDirection) {
          setSelectedDirection(directionToUse);
          setIsAutoDetected(true);
        }
      }

      // Use same-origin /api/translate so the server proxy adds the API token (token never in browser or URL)
      const response = await fetch("/api/translate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mod: directionToUse,
          text: textToTranslate,
          ignoreHtml: true,
        }),
      });

      console.log("Response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse = await response.json();
      console.log("API response:", data);

      if (data.message === "success" && data.result) {
        console.log(
          "Setting output text:",
          data.result.substring(0, 100) + "...",
        );
        setOutputText(data.result);
      } else if (data.result) {
        console.log(
          "Setting output text (fallback):",
          data.result.substring(0, 100) + "...",
        );
        setOutputText(data.result);
      } else {
        console.log("No result, setting error message");
        setOutputText(JSON.stringify(data));
      }
    } catch (error) {
      console.error("Translation error:", error);
      setOutputText(t("homePage.translationError"));
    } finally {
      setIsLoading(false);
    }
  }, [inputText, selectedDirection]);

  // handleTranslit changes on every keystroke; SidePanel is memoized, so it
  // gets a wrapper that stays the same and always calls the latest version.
  const translitRef = useRef(handleTranslit);
  useEffect(() => {
    translitRef.current = handleTranslit;
  }, [handleTranslit]);
  const stableTranslit = useCallback(() => translitRef.current(), []);

  // Manual direction o'zgartirganda auto-detect flagini o'chirish
  const handleDirectionChange = useCallback((direction: string) => {
    setSelectedDirection(direction);
    setIsAutoDetected(false);
  }, []);

  // Direction switcher handlers for EditorPanel
  const handleFromLanguageChange = (key: string) => {
    if (isScript(key)) handleDirectionChange(withFrom(selectedDirection, key));
  };

  const handleToLanguageChange = (key: string) => {
    if (isScript(key)) handleDirectionChange(withTo(selectedDirection, key));
  };

  const handleDirectionSwap = () => {
    setOutputText("");
    setInputText(outputText);
    handleDirectionChange(swapDirection(selectedDirection));
  };

  return (
    <Layout title="Главная">
      <div className="editors-container flex-1 flex lg:min-h-0  flex-col lg:flex-row">
        <EditorPanel
          inputText={inputText}
          setInputText={setInputText}
          outputText={outputText}
          setOutputText={setOutputText}
          handleTranslit={handleTranslit}
          selectedDirection={selectedDirection}
          onFromLanguageChange={handleFromLanguageChange}
          onToLanguageChange={handleToLanguageChange}
          onDirectionSwap={handleDirectionSwap}
        />
        <SidePanel
          selectedDirection={selectedDirection}
          setSelectedDirection={handleDirectionChange}
          isLoading={isLoading}
          handleTranslit={stableTranslit}
          isAutoDetected={isAutoDetected}
          setInputText={setInputText}
          setOutputText={setOutputText}
          outputText={outputText}
        />
      </div>
    </Layout>
  );
}
